import { Box, Typography, Grid, Card } from "@mui/material";
import { useMemo, useState, useCallback } from "react";
import { ReportValueCardProps } from "helpers/types";
import { drawdownComparisonChartStyles } from "../../../../styles/DrawdownComparisonChart.styles";
import { DrawdownComparisonEditModal } from "./DrawdownComparisonEditModal";
import {
  getBarDirection,
  getBarOffset,
  getBarWidth,
  getBarWidthPercentage,
} from "./comparisonChartUtils";

const DEFAULT_DRAWDOWN_LIMIT = 20;

interface DrawdownComparisonChartProps {
  values: ReportValueCardProps[];
  limit?: number;
  negativeLimit?: number;
  onLimitChange?: (limit: number) => void;
  onNegativeLimitChange?: (negativeLimit: number) => void;
}

const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

export const DrawdownComparisonChart = ({
  values,
  limit: controlledLimit,
  negativeLimit: controlledNegativeLimit,
  onLimitChange,
  onNegativeLimitChange,
}: DrawdownComparisonChartProps) => {
  const [internalMaxDrop, setInternalMaxDrop] = useState(
    DEFAULT_DRAWDOWN_LIMIT,
  );
  const [internalNegativeLimit, setInternalNegativeLimit] = useState(
    -DEFAULT_DRAWDOWN_LIMIT,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const maxDrop = controlledLimit ?? internalMaxDrop;
  const negativeLimit = controlledNegativeLimit ?? internalNegativeLimit;

  const stockDrop = useMemo(() => {
    const stockValue = values.find((v) => v.label === "Max Stock Drop");
    return typeof stockValue?.value === "number"
      ? stockValue.value
      : parseFloat(stockValue?.value.toString() || "0") || 0;
  }, [values]);

  const oeaDrop = useMemo(() => {
    const oeaValue = values.find((v) => v.label === "OEA Drop");
    return typeof oeaValue?.value === "number"
      ? oeaValue.value
      : parseFloat(oeaValue?.value.toString() || "0") || 0;
  }, [values]);

  const handleClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleMaxDropChange = useCallback(
    (newMaxDrop: number) => {
      if (onLimitChange) {
        onLimitChange(newMaxDrop);
      } else {
        setInternalMaxDrop(newMaxDrop);
      }
    },
    [onLimitChange],
  );

  const handleNegativeLimitChange = useCallback(
    (newNegativeLimit: number) => {
      if (onNegativeLimitChange) {
        onNegativeLimitChange(newNegativeLimit);
      } else {
        setInternalNegativeLimit(newNegativeLimit);
      }
    },
    [onNegativeLimitChange],
  );

  const negativeLimitMagnitude = Math.abs(negativeLimit);

  const stockPercentage = useMemo(
    () => getBarWidthPercentage(stockDrop, maxDrop, negativeLimitMagnitude),
    [stockDrop, maxDrop, negativeLimitMagnitude],
  );

  const oeaPercentage = useMemo(
    () => getBarWidthPercentage(oeaDrop, maxDrop, negativeLimitMagnitude),
    [oeaDrop, maxDrop, negativeLimitMagnitude],
  );

  const hasMixedSigns =
    (stockDrop < 0 && oeaDrop > 0) || (stockDrop > 0 && oeaDrop < 0);

  const stockFilledWidth = useMemo(
    () => getBarWidth(stockPercentage, hasMixedSigns),
    [stockPercentage, hasMixedSigns],
  );

  const oeaFilledWidth = useMemo(
    () => getBarWidth(oeaPercentage, hasMixedSigns),
    [oeaPercentage, hasMixedSigns],
  );

  const stockBarDirection = getBarDirection(stockDrop);
  const oeaBarDirection = getBarDirection(oeaDrop);

  const rows = [
    {
      key: "stock",
      label: "Stock",
      value: stockDrop,
      width: stockFilledWidth,
      direction: stockBarDirection,
      filledStyle: drawdownComparisonChartStyles.barStockFilled,
      valueStyle: drawdownComparisonChartStyles.barStockValue,
      rowSx: drawdownComparisonChartStyles.barRow,
    },
    {
      key: "oea",
      label: "OEA",
      value: oeaDrop,
      width: oeaFilledWidth,
      direction: oeaBarDirection,
      filledStyle: drawdownComparisonChartStyles.barOeaFilled,
      valueStyle: drawdownComparisonChartStyles.barOeaValue,
      rowSx: {
        ...drawdownComparisonChartStyles.barRow,
        marginBottom: 0,
      },
    },
  ] as const;

  return (
    <>
      <Card
        sx={drawdownComparisonChartStyles.card}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label="Click to edit drawdown limit"
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs="auto">
            <Box sx={drawdownComparisonChartStyles.titleContainer}>
              <Typography sx={drawdownComparisonChartStyles.titleLine1}>
                Drawdown
              </Typography>
              <Typography sx={drawdownComparisonChartStyles.titleLine2}>
                Comparison
              </Typography>
            </Box>
          </Grid>
          <Grid item xs>
            <Box sx={drawdownComparisonChartStyles.barsContainer}>
              {rows.map((row) => (
                <Box key={row.key} sx={row.rowSx}>
                  <Typography sx={drawdownComparisonChartStyles.barLabel}>
                    {row.label}
                  </Typography>
                  <Box sx={drawdownComparisonChartStyles.barContainer}>
                    <Box
                      sx={{
                        ...row.filledStyle,
                        width: row.width,
                        left:
                          row.direction === "left"
                            ? getBarOffset(hasMixedSigns)
                            : "auto",
                        right:
                          row.direction === "right"
                            ? getBarOffset(hasMixedSigns)
                            : "auto",
                      }}
                    />
                  </Box>
                  <Typography sx={row.valueStyle}>
                    {formatPercentage(row.value)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Card>

      <DrawdownComparisonEditModal
        isOpened={isModalOpen}
        onClose={handleCloseModal}
        maxDrop={maxDrop}
        negativeLimit={negativeLimit}
        onMaxDropChange={handleMaxDropChange}
        onNegativeLimitChange={handleNegativeLimitChange}
      />
    </>
  );
};
